import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ChemicalProperty} from 'chemistry/ChemistryConstants';
import {MoleculePhysicochemicalPropertiesQuery} from 'chemistry/molecules/tabs/__generated/MoleculePhysicochemicalPropertiesQuery';
import DataConnector from 'connect/DataConnector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {MoleculePropertyItem, MoleculePropertyItemProps} from './MoleculePropertyItem';

type chemicalStructure = ExtractByTypename<
  MoleculePhysicochemicalPropertiesQuery['molecule'],
  'MoleculeNode'
>['version']['chemicalStructure'];

// Keep in sync with DECIMAL_PRECISION in benchling/schemas/computed_fields/formulas/chemistry.py
const DECIMAL_PRECISION = 3;

const MOLECULE_FLOAT_PROPERTY_FRAGMENT = gql`
  fragment MoleculeFloatProperty on FloatPropertyOrErrorNode {
    ... on FloatPropertyNode {
      value
    }
    ... on ErrorNode {
      message
    }
  }
`;

const MOLECULE_INT_PROPERTY_FRAGMENT = gql`
  fragment MoleculeIntProperty on IntPropertyOrErrorNode {
    ... on IntPropertyNode {
      value
    }
    ... on ErrorNode {
      message
    }
  }
`;

export const MOLECULE_PHYSICOCHEMICAL_PROPERTIES_QUERY = gql`
  query MoleculePhysicochemicalPropertiesQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            molecularWeight {
              ... on MolecularWeightNode {
                value
              }
              ... on ErrorNode {
                message
              }
            }
            exactMolecularWeight {
              ...MoleculeFloatProperty
            }
            formulaWeight {
              ...MoleculeFloatProperty
            }
            exactFormulaWeight {
              ...MoleculeFloatProperty
            }
            atomCount {
              ...MoleculeIntProperty
            }
            heavyAtomCount {
              ...MoleculeIntProperty
            }
            hydrogenAcceptorCount {
              ...MoleculeIntProperty
            }
            hydrogenDonorCount {
              ...MoleculeIntProperty
            }
            logp {
              ...MoleculeFloatProperty
            }
            molarRefractivity {
              ...MoleculeFloatProperty
            }
            rotatableBondCount {
              ...MoleculeIntProperty
            }
            tpsa {
              ...MoleculeFloatProperty
            }
            aromaticRingCount {
              ...MoleculeIntProperty
            }
            ringCount {
              ...MoleculeIntProperty
            }
            sp3AtomCount {
              ...MoleculeIntProperty
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${MOLECULE_FLOAT_PROPERTY_FRAGMENT}
  ${MOLECULE_INT_PROPERTY_FRAGMENT}
`;

interface ConnectedMoleculePhysicochemicalPropertiesProps {
  moleculeVersionId: string;
}

interface UnconnectedMoleculePhysicochemicalPropertiesProps {
  moleculeId: string;
}

type MoleculePhysicochemicalPropertiesProps = ConnectedMoleculePhysicochemicalPropertiesProps &
  UnconnectedMoleculePhysicochemicalPropertiesProps;

class MoleculePhysicochemicalPropertiesConnector extends DataConnector<
  UnconnectedMoleculePhysicochemicalPropertiesProps,
  ConnectedMoleculePhysicochemicalPropertiesProps
> {
  enhanceProps(): ConnectedMoleculePhysicochemicalPropertiesProps {
    const moleculeVersionId =
      this.reduxState().ui.chemistry.moleculeVersionUIs[this.props.moleculeId].moleculeVersionId;
    return {moleculeVersionId};
  }
}

function getMoleculePropertyItemPropsFromStructure(
  structure: chemicalStructure
): Array<MoleculePropertyItemProps> {
  const result: Array<MoleculePropertyItemProps> = [];

  const {
    molecularWeight,
    exactMolecularWeight,
    formulaWeight,
    exactFormulaWeight,
    atomCount,
    heavyAtomCount,
    sp3AtomCount,
    hydrogenAcceptorCount,
    hydrogenDonorCount,
    logp,
    ringCount,
    aromaticRingCount,
    rotatableBondCount,
    tpsa,
    molarRefractivity,
  } = structure;

  const propertyWithPropertyData = [
    {chemicalProperty: ChemicalProperty.MOLECULAR_WEIGHT, propertyData: molecularWeight},
    {chemicalProperty: ChemicalProperty.EXACT_MOLECULAR_WEIGHT, propertyData: exactMolecularWeight},
    ...(BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE
      ? [
          {chemicalProperty: ChemicalProperty.FORMULA_WEIGHT, propertyData: formulaWeight},
          {chemicalProperty: ChemicalProperty.EXACT_FORMULA_WEIGHT, propertyData: exactFormulaWeight},
        ]
      : []),
    {chemicalProperty: ChemicalProperty.ATOM_COUNT, propertyData: atomCount},
    {chemicalProperty: ChemicalProperty.HEAVY_ATOM_COUNT, propertyData: heavyAtomCount},
    {chemicalProperty: ChemicalProperty.SP3_ATOM_COUNT, propertyData: sp3AtomCount},
    {chemicalProperty: ChemicalProperty.HYDROGEN_ACCEPTOR_COUNT, propertyData: hydrogenAcceptorCount},
    {chemicalProperty: ChemicalProperty.HYDROGEN_DONOR_COUNT, propertyData: hydrogenDonorCount},
    {chemicalProperty: ChemicalProperty.RING_COUNT, propertyData: ringCount},
    {chemicalProperty: ChemicalProperty.NUM_AROMATIC_RINGS, propertyData: aromaticRingCount},
    {chemicalProperty: ChemicalProperty.ROTATABLE_BOND_COUNT, propertyData: rotatableBondCount},
    {chemicalProperty: ChemicalProperty.LOGP, propertyData: logp},
    {chemicalProperty: ChemicalProperty.TOPOLOGICAL_POLAR_SURFACE_AREA, propertyData: tpsa},
    {chemicalProperty: ChemicalProperty.MOLAR_REFRACTIVITY, propertyData: molarRefractivity},
  ];

  propertyWithPropertyData.forEach(({chemicalProperty, propertyData}) => {
    if (propertyData.__typename === 'ErrorNode') {
      result.push({chemicalProperty, value: null, error: propertyData.message});
    } else {
      const value =
        propertyData.__typename === 'FloatPropertyNode' || propertyData.__typename === 'MolecularWeightNode'
          ? propertyData.value.toFixed(DECIMAL_PRECISION)
          : propertyData.value;
      result.push({chemicalProperty, value, error: null});
    }
  });
  return result;
}

export function MoleculePhysicochemicalProperties(
  props: MoleculePhysicochemicalPropertiesProps
): JSX.Element | null {
  return (
    <BenchlingQuery
      query={MOLECULE_PHYSICOCHEMICAL_PROPERTIES_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data: MoleculePhysicochemicalPropertiesQuery) => {
        if (isInaccessible(data.molecule)) {
          return (
            <div>You do not have access to view this molecule. Please ask your administrator for access.</div>
          );
        }

        return (
          <div>
            <div className="simpleDivTable">
              <div className={`simpleDivTable-header ${css(styles.smallHeader)}`}>
                <div className={`simpleDivTable-headerCell ${css(styles.propertyNameColumn)}`}>PROPERTY</div>
                <div className={`simpleDivTable-headerCell ${css(styles.valueColumn)}`}>VALUE</div>
              </div>
              {getMoleculePropertyItemPropsFromStructure(data.molecule.version.chemicalStructure).map(
                ({chemicalProperty, value, error}) => (
                  <MoleculePropertyItem
                    key={chemicalProperty}
                    chemicalProperty={chemicalProperty}
                    value={value}
                    error={error}
                  />
                )
              )}
            </div>
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

export const ConnectedMoleculePhysicochemicalProperties = MoleculePhysicochemicalPropertiesConnector.connect(
  MoleculePhysicochemicalProperties
);

const styles = StyleSheet.create({
  propertyNameColumn: {
    flex: 1,
  },
  valueColumn: {
    flex: 2,
  },
  smallHeader: {
    ...FONT_PROXIMA_11,
  },
});
